import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  FlaskConical, Play, Pause, CheckCircle, XCircle, 
  TrendingUp, Users, Building2, BarChart3, Plus,
  AlertTriangle, Target, Clock, ArrowUpRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import CreateExperimentWizard from '@/components/admin/CreateExperimentWizard';

interface Experiment {
  id: string;
  name: string;
  description: string;
  intervention_type: string;
  is_active: boolean;
  start_date: string;
  end_date: string | null;
  company_id: string | null;
  treatment_group_size: number;
  control_group_size: number;
  effect_size: number | null;
  p_value: number | null;
  conclusion: string | null;
  variant_a: Record<string, any>;
  variant_b: Record<string, any>;
  baseline_metrics: Record<string, any>;
  outcome_metrics: Record<string, any>;
  companies?: { company_name: string } | null;
}

interface ExperimentStats {
  activeExperiments: number;
  completedExperiments: number;
  avgEffectSize: number;
  totalParticipants: number;
}

export default function SuperAdminInterventions() {
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [stats, setStats] = useState<ExperimentStats>({
    activeExperiments: 0,
    completedExperiments: 0,
    avgEffectSize: 0,
    totalParticipants: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedExperiment, setSelectedExperiment] = useState<Experiment | null>(null);
  const [showCreateWizard, setShowCreateWizard] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: experimentsData, error } = await supabase
        .from('intervention_experiments')
        .select(`
          *,
          companies (company_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const typedExperiments = (experimentsData || []) as unknown as Experiment[];
      setExperiments(typedExperiments);

      // Calculate stats
      const active = typedExperiments.filter(e => e.is_active).length;
      const completed = typedExperiments.filter(e => !e.is_active && e.conclusion).length;
      const withEffectSize = typedExperiments.filter(e => e.effect_size !== null);
      const avgEffect = withEffectSize.length > 0 
        ? withEffectSize.reduce((sum, e) => sum + (e.effect_size || 0), 0) / withEffectSize.length 
        : 0;
      const totalParticipants = typedExperiments.reduce(
        (sum, e) => sum + (e.treatment_group_size || 0) + (e.control_group_size || 0), 0
      );

      setStats({
        activeExperiments: active,
        completedExperiments: completed,
        avgEffectSize: avgEffect,
        totalParticipants,
      });
    } catch (error) {
      console.error('Error loading experiments:', error);
      toast.error('Failed to load experiments');
    } finally {
      setLoading(false);
    }
  };

  const activeExperiments = experiments.filter(e => e.is_active);
  const completedExperiments = experiments.filter(e => !e.is_active);

  const getStatusBadge = (experiment: Experiment) => {
    if (experiment.is_active) {
      return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Active</Badge>;
    }
    if (experiment.conclusion) {
      const isPositive = (experiment.effect_size || 0) > 0 && (experiment.p_value || 1) < 0.05;
      return isPositive 
        ? <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Significant</Badge>
        : <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">Inconclusive</Badge>;
    }
    return <Badge variant="secondary">Draft</Badge>;
  };

  const formatPValue = (pValue: number | null) => {
    if (pValue === null) return '-';
    if (pValue < 0.001) return '< 0.001';
    if (pValue < 0.01) return '< 0.01';
    if (pValue < 0.05) return '< 0.05';
    return pValue.toFixed(3);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FlaskConical className="h-6 w-6 text-primary" />
            Research Experiments
          </h2>
          <p className="text-muted-foreground">
            Design and run behavioral intervention experiments with proper control groups
          </p>
        </div>
        <Button onClick={() => setShowCreateWizard(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Experiment
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Play className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{stats.activeExperiments}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">{stats.completedExperiments}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <TrendingUp className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Effect Size</p>
                <p className="text-2xl font-bold">{stats.avgEffectSize.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Users className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Participants</p>
                <p className="text-2xl font-bold">{stats.totalParticipants}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Experiments Tabs */}
      <Tabs defaultValue="active" className="space-y-4">
        <TabsList>
          <TabsTrigger value="active">Active ({activeExperiments.length})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({completedExperiments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {activeExperiments.length === 0 ? (
            <Card className="p-8 text-center">
              <FlaskConical className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">No Active Experiments</h3>
              <p className="text-muted-foreground mb-4">
                Start a new experiment to test behavioral interventions
              </p>
              <Button onClick={() => setShowCreateWizard(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Experiment
              </Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {activeExperiments.map((experiment) => (
                <ExperimentCard 
                  key={experiment.id} 
                  experiment={experiment}
                  onClick={() => setSelectedExperiment(experiment)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {completedExperiments.length === 0 ? (
            <Card className="p-8 text-center">
              <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No completed experiments yet</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {completedExperiments.map((experiment) => (
                <ExperimentCard 
                  key={experiment.id} 
                  experiment={experiment}
                  onClick={() => setSelectedExperiment(experiment)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Experiment Detail Dialog */}
      <Dialog open={!!selectedExperiment} onOpenChange={() => setSelectedExperiment(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedExperiment?.name}</DialogTitle>
          </DialogHeader>
          {selectedExperiment && (
            <ExperimentDetail experiment={selectedExperiment} />
          )}
        </DialogContent>
      </Dialog>

      {/* Create Experiment Wizard */}
      <Dialog open={showCreateWizard} onOpenChange={setShowCreateWizard}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Experiment</DialogTitle>
          </DialogHeader>
          <CreateExperimentWizard 
            onComplete={() => {
              setShowCreateWizard(false);
              loadData();
            }}
            onCancel={() => setShowCreateWizard(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExperimentCard({ experiment, onClick }: { experiment: Experiment; onClick: () => void }) {
  const progress = experiment.end_date 
    ? Math.min(100, ((Date.now() - new Date(experiment.start_date).getTime()) / 
        (new Date(experiment.end_date).getTime() - new Date(experiment.start_date).getTime())) * 100)
    : 0;

  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold">{experiment.name}</h3>
              {experiment.is_active && (
                <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Active</Badge>
              )}
              {!experiment.is_active && experiment.conclusion && (
                <Badge className={cn(
                  (experiment.effect_size || 0) > 0 && (experiment.p_value || 1) < 0.05
                    ? "bg-green-500/10 text-green-500 border-green-500/20"
                    : "bg-amber-500/10 text-amber-500 border-amber-500/20"
                )}>
                  {(experiment.effect_size || 0) > 0 && (experiment.p_value || 1) < 0.05 ? 'Significant' : 'Inconclusive'}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">{experiment.description}</p>
            
            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Target className="h-4 w-4" />
                <span>{experiment.intervention_type}</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span>{(experiment.treatment_group_size || 0) + (experiment.control_group_size || 0)} participants</span>
              </div>
              {experiment.companies?.company_name && (
                <div className="flex items-center gap-1">
                  <Building2 className="h-4 w-4" />
                  <span>{experiment.companies.company_name}</span>
                </div>
              )}
            </div>

            {experiment.is_active && experiment.end_date && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Progress</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-1.5" />
              </div>
            )}
          </div>

          <div className="text-right">
            {experiment.effect_size !== null && (
              <div>
                <p className="text-xs text-muted-foreground">Effect Size</p>
                <p className={cn(
                  "text-lg font-bold",
                  experiment.effect_size > 0 ? "text-green-500" : "text-red-500"
                )}>
                  {experiment.effect_size > 0 ? '+' : ''}{experiment.effect_size.toFixed(2)}
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ExperimentDetail({ experiment }: { experiment: Experiment }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Treatment Group</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{experiment.treatment_group_size || 0}</p>
            <p className="text-xs text-muted-foreground">participants</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Control Group</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{experiment.control_group_size || 0}</p>
            <p className="text-xs text-muted-foreground">participants</p>
          </CardContent>
        </Card>
      </div>

      {experiment.effect_size !== null && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Statistical Results</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Effect Size</p>
              <p className={cn(
                "text-xl font-bold",
                experiment.effect_size > 0 ? "text-green-500" : "text-red-500"
              )}>
                {experiment.effect_size > 0 ? '+' : ''}{experiment.effect_size.toFixed(3)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">P-Value</p>
              <p className={cn(
                "text-xl font-bold",
                (experiment.p_value || 1) < 0.05 ? "text-green-500" : "text-muted-foreground"
              )}>
                {experiment.p_value !== null ? (experiment.p_value < 0.001 ? '< 0.001' : experiment.p_value.toFixed(3)) : '-'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Significance</p>
              <p className="text-xl font-bold">
                {(experiment.p_value || 1) < 0.05 ? '✓ Yes' : '✗ No'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {experiment.conclusion && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Conclusion</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{experiment.conclusion}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Variant A (Treatment)</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-secondary p-2 rounded overflow-auto max-h-32">
              {JSON.stringify(experiment.variant_a, null, 2)}
            </pre>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Variant B (Control)</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-secondary p-2 rounded overflow-auto max-h-32">
              {JSON.stringify(experiment.variant_b, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
